import React from 'react';

import { useFormikContext } from 'formik';

import { ENTRY_TYPE } from '../index';
import { ResourceSubmissionFieldHint } from '../styled';

interface ResourceSubmissionLinkHintProps {
  resourceFormat?: string | null;
}

const GOOGLE_SLIDES = 'Google Slides';

// Editor files may carry a legacy Workspace segment: docs.google.com/a/DOMAIN/document/...
const docsPath = (type: string) => new RegExp(`^/(a/[^/]+/)?${type}(/|$)`, 'i');

const GOOGLE_PRODUCTS: { hosts: string[]; path?: RegExp; name: string }[] = [
  { hosts: ['docs.google.com'], path: docsPath('presentation'), name: GOOGLE_SLIDES },
  { hosts: ['docs.google.com'], path: docsPath('document'), name: 'Google Docs' },
  { hosts: ['docs.google.com'], path: docsPath('spreadsheets'), name: 'Google Sheets' },
  { hosts: ['docs.google.com'], path: docsPath('forms'), name: 'Google Forms' },
  { hosts: ['forms.gle'], name: 'Google Forms' },
  { hosts: ['docs.google.com'], path: docsPath('drawings'), name: 'Google Drawings' },
  // Legacy Drive links still served from docs.google.com: /file/d/ID, /uc?id=ID, /open?id=ID
  { hosts: ['docs.google.com'], path: docsPath('(file|uc|open)'), name: 'Google Drive' },
  { hosts: ['drive.google.com', 'drive.usercontent.google.com'], name: 'Google Drive' },
  { hosts: ['sites.google.com'], name: 'Google Sites' },
];

const parseLink = (link: string): URL | null => {
  try {
    return new URL(/^https?:\/\//i.test(link) ? link : `https://${link}`);
  } catch {
    return null;
  }
};

export const getGoogleProductName = (
  link?: string | null,
  resourceFormat?: string | null
): string | null => {
  const url = link ? parseLink(link.trim()) : null;
  const product =
    url &&
    GOOGLE_PRODUCTS.find(
      ({ hosts, path }) =>
        hosts.includes(url.hostname.toLowerCase()) && (!path || path.test(url.pathname))
    );
  if (product) return product.name;

  return resourceFormat?.toLowerCase() === ENTRY_TYPE.GOOGLE_SLIDES ? GOOGLE_SLIDES : null;
};

export default function ResourceSubmissionLinkHint({
  resourceFormat,
}: ResourceSubmissionLinkHintProps) {
  const { values } = useFormikContext<{ link?: string | null }>();
  const productName = getGoogleProductName(values.link, resourceFormat);

  if (!productName) return null;

  return (
    <ResourceSubmissionFieldHint>
      Make sure your {productName} file is public (anyone with the link can view), otherwise it
      won't display for others.
    </ResourceSubmissionFieldHint>
  );
}
